import { find, round } from 'lodash';
import React, { useContext } from 'react';
import ReactTooltip from 'react-tooltip';
import { assertNotNull } from '../../../../../../helpers/nullHelpers';
import { ReactComponent as UpdateDisabled } from '../../../../../../images/icons/update_disabled.svg';
import { TextInputField } from '../../../../../../shared/form/inputs/TextInputField';
import { ReadOnlyField } from '../../../../../../shared/form/ReadOnlyField';
import { DesignCalculationReadOnlyField } from '../../../../../designs/design-form/calculations/DesignCalculationReadOnlyField';
import { ManifoldFormModel } from '../../../manifolds/manifoldFormModel';
import { QuoteOrDesignFormContext } from '../../../QuoteOrDesignFormContext';
import { manifoldFieldName, roomFieldName } from '../../../quoteOrDesignFormFieldNameHelpers';
import { UnitFieldContext } from '../../../units/UnitFieldContext';
import { calculateEstimatedLengthOfEachPipeInMetresForRoom } from '../../roomCalculations';
import { RoomFormModel } from '../../roomFormModel';
import { RoomAutocompleteItem, RoomType } from '../../roomTypes';
import { RoomFloorConstructionField } from '../RoomFloorConstructionField';
import { RoomNameField } from '../RoomNameField';
import { RoomNumberOfPortsField } from '../RoomNumberOfPortsField';

// The Performance fields for a Room are:
// * Room Name
// * Floor Construction
// * Number of Ports
// * Pipes per Port
// * Estimated Length
// * kPa Loss per Coil
// * ∆T
// * Floor Temperature
// * Max Temperature
// * % Coverage
// * Shortfall

type Props = {
  unitIndex: number;
  manifoldIndex: number;
  manifold: ManifoldFormModel;
  roomIndex: number;
  isWithinRadiatorManifold: boolean;
  room: RoomFormModel;
  handleSelectionOfRoomName: (selectedItem: RoomAutocompleteItem) => void;
};

export const RoomFieldsPerformance = ({
  unitIndex,
  manifoldIndex,
  manifold,
  roomIndex,
  isWithinRadiatorManifold,
  room,
  handleSelectionOfRoomName,
}: Props) => {
  const unitFieldContext = assertNotNull(useContext(UnitFieldContext));

  const { designCalculations } = unitFieldContext;

  const unitDesignCalculationsResponse = designCalculations && designCalculations.response;
  const designCalculationsInProgress = designCalculations != null && designCalculations.inProgress;
  const designCalculationsError = designCalculations && designCalculations.error;

  const roomDesignCalculationsResult =
    (unitDesignCalculationsResponse &&
      find(
        unitDesignCalculationsResponse.rooms,
        r => r.manifoldIndex === manifoldIndex && r.roomIndex === roomIndex,
      )) ||
    null;

  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue } = formContext?.formProps;

  const estimatedLengthOfEachPipeInMetres = room.designRoomDetails?.estimatedLengthOfEachPipeInMetres ?? null;

  const roomAreaInMetresSquared = room.areaInMetresSquared;
  const voidAreaInMetresSquared = room.designRoomDetails?.voidAreaInMetresSquared ?? '';
  const unheatedAreaInMetresSquared = room.designRoomDetails?.unheatedAreaInMetresSquared ?? '';
  const floorConstruction = room.floorConstruction;
  const manifoldDistanceInMetres = room.designRoomDetails?.manifoldDistanceInMetres ?? '';
  const verticalRunbackInMetres = room.designRoomDetails?.verticalRunbackInMetres ?? '';
  const numberOfPorts = room.numberOfPorts;
  const pipesPerPort = room.designRoomDetails?.pipesPerPort ?? '';

  const onChangeNumberOfPortsOrPipesPerPort = (newNumberOfPorts: string, newPipesPerPort: string) => {
    const newEstimatedLengthOfEachPipeInMetres = calculateEstimatedLengthOfEachPipeInMetresForRoom({
      roomAreaInMetresSquared,
      voidAreaInMetresSquared,
      unheatedAreaInMetresSquared,
      floorConstruction,
      manifoldDistanceInMetres,
      verticalRunbackInMetres,
      numberOfPorts: newNumberOfPorts,
      pipesPerPort: newPipesPerPort,
    });

    setFieldValue(
      roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.estimatedLengthOfEachPipeInMetres'),
      newEstimatedLengthOfEachPipeInMetres,
    );
  };

  const onChangeNumberOfPorts = (newNumberOfPorts: string) => {
    onChangeNumberOfPortsOrPipesPerPort(newNumberOfPorts, pipesPerPort);
  };

  const onChangePipesPerPort = (newPipesPerPort: string) => {
    onChangeNumberOfPortsOrPipesPerPort(numberOfPorts, newPipesPerPort);
  };

  const disableNumberOfPortsAndPipesPerPortCalculation = () =>
    setFieldValue(
      manifoldFieldName(
        unitIndex,
        manifoldIndex,
        'designManifoldDetails.disableNumberOfPortsAndPipesPerPortCalculation',
      ),
      true,
    );

  const roomPressureLossFieldColour =
    roomDesignCalculationsResult && roomDesignCalculationsResult.pressureLossPerCoil
      ? roomDesignCalculationsResult.pressureLossPerCoil >= -20
        ? 'green'
        : roomDesignCalculationsResult.pressureLossPerCoil < -30
        ? 'red'
        : 'yellow'
      : null;

  const roomFloorTempFieldColour =
    roomDesignCalculationsResult &&
    roomDesignCalculationsResult.heatOutput &&
    room.designRoomDetails &&
    room.designRoomDetails.maximumTemperatureInDegreesCelsius
      ? roomDesignCalculationsResult.heatOutput.floorTemperatureInDegreesCelsius <
        Number(room.designRoomDetails.maximumTemperatureInDegreesCelsius)
        ? 'green'
        : 'red'
      : null;

  const roomShortfallFieldColour =
    roomDesignCalculationsResult && roomDesignCalculationsResult.heatOutput
      ? roomDesignCalculationsResult.heatOutput.shortfallInWatts <= 0
        ? 'green'
        : 'red'
      : null;

  const roomPercentageCoverageFieldColour =
    roomDesignCalculationsResult && roomDesignCalculationsResult.heatOutput
      ? roomDesignCalculationsResult.heatOutput.percentageCoverage > 120
        ? 'green'
        : roomDesignCalculationsResult.heatOutput.percentageCoverage < 100
        ? 'red'
        : 'yellow'
      : null;

  return (
    <>
      <RoomNameField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        handleSelectionOfRoomName={handleSelectionOfRoomName}
      />
      <RoomFloorConstructionField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        isWithinRadiatorManifold={isWithinRadiatorManifold}
      />
      <RoomNumberOfPortsField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        manifold={manifold}
        roomIndex={roomIndex}
        isWithinRadiatorManifold={isWithinRadiatorManifold}
        room={room}
        onChange={onChangeNumberOfPorts}
        onInput={disableNumberOfPortsAndPipesPerPortCalculation}
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.pipesPerPort')}
        label="Pipes per Port"
        labelIcon={
          manifold.designManifoldDetails?.disableNumberOfPortsAndPipesPerPortCalculation ? (
            <>
              <UpdateDisabled data-tip="Automatic calculation disabled" />
              <ReactTooltip effect="solid" clickable={true} />
            </>
          ) : null
        }
        onChange={onChangePipesPerPort}
        onInput={disableNumberOfPortsAndPipesPerPortCalculation}
      />
      <ReadOnlyField
        label="Estimated Length (m)"
        value={estimatedLengthOfEachPipeInMetres == null ? '' : round(estimatedLengthOfEachPipeInMetres, 2).toString()}
      />
      {!isWithinRadiatorManifold &&
        room.floorConstruction != null &&
        !room.floorConstruction.isElectricUnderfloorHeating &&
        !room.floorConstruction.isSkirtingHeating && (
          <DesignCalculationReadOnlyField
            label="kPa Loss per Coil"
            designCalculationsInProgress={designCalculationsInProgress}
            designCalculationsError={designCalculationsError}
            value={roomDesignCalculationsResult?.pressureLossPerCoil?.toLocaleString() ?? null}
            colour={roomPressureLossFieldColour}
          />
        )}
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.temperatureDeltaInDegreesCelsius')}
        label="∆T (°C)"
      />
      <DesignCalculationReadOnlyField
        label="Floor Temp. (°C)"
        designCalculationsInProgress={designCalculationsInProgress}
        designCalculationsError={designCalculationsError}
        value={roomDesignCalculationsResult?.heatOutput?.floorTemperatureInDegreesCelsius.toLocaleString() ?? null}
        colour={roomFloorTempFieldColour}
      />
      <TextInputField
        name={roomFieldName(
          unitIndex,
          manifoldIndex,
          roomIndex,
          'designRoomDetails.maximumTemperatureInDegreesCelsius',
        )}
        label="Max Temp. (°C)"
      />
      <DesignCalculationReadOnlyField
        label="% Coverage"
        designCalculationsInProgress={designCalculationsInProgress}
        designCalculationsError={designCalculationsError}
        value={roomDesignCalculationsResult?.heatOutput?.percentageCoverage.toLocaleString() ?? null}
        colour={roomPercentageCoverageFieldColour}
      />
      <DesignCalculationReadOnlyField
        label="Shortfall (W)"
        designCalculationsInProgress={designCalculationsInProgress}
        designCalculationsError={designCalculationsError}
        value={roomDesignCalculationsResult?.heatOutput?.shortfallInWatts.toLocaleString() ?? null}
        colour={roomShortfallFieldColour}
      />
    </>
  );
};
